import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";
import { LOCATIONS } from "../constants";
import COLORS from "../colors";
import { every, isEmpty } from "lodash";
import { diff } from "deep-object-diff";

export const VEHICLE_FIELDS = {
  // Vehicle info
  type: "",
  name: "",
  location: null,
  mileage: null,
  lastService: null,
  status: "",

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(VEHICLE_FIELDS);

export const VEHICLE_TYPES = {
  delivery: {
    label: "Delivery",
    icon: "vehicle_delivery",
  },
  service: {
    label: "Service",
    icon: "vehicle_service",
  },
  personal: {
    label: "Personal",
    icon: "vehicle_personal",
  },
};
deepFreeze(VEHICLE_TYPES);

export const VEHICLE_STATUS_TYPES = {
  ready: {
    order: 0,
    label: "Ready to drive",
    icon: "done",
    color: "positive",
  },
  maintenance: {
    order: 1,
    label: "Maintenance required",
    icon: "maintenance",
    color: "warning",
  },
  hold: {
    order: 2,
    label: "Out of commission",
    icon: "cancel",
    color: "negative",
  },
};
deepFreeze(VEHICLE_STATUS_TYPES);

export const VEHICLE_LOCATIONS = [
  ...LOCATIONS,
  {
    label: "Personal Residence",
    value: "personal",
  },
];
deepFreeze(VEHICLE_LOCATIONS);

export default class Vehicle extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, VEHICLE_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      view: Vehicle.canBeViewedBy,
      create: Vehicle.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user) {
    return user.isSuper;
  }

  static canBeCreatedBy(user) {
    return user.isSuper;
  }

  canBeEditedBy(user) {
    return user.isSuper;
  }

  canBeDeletedBy(user) {
    return user.isAdmin;
  }

  // Database

  get path() {
    return Vehicle.getPath(this.id);
  }

  static getPath(id) {
    let path = `vehicles`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(vehicle) {
    return vehicle.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Vehicle(snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      type: [
        (a) => Boolean(a) || "required",
        (t) => t in VEHICLE_TYPES || "vehicleTypeInvalid",
      ],
      name: [(a) => Boolean(a && a.trim()) || "required"],
      location: [
        (a) => Boolean(a) || "required",
        (l) =>
          Boolean(VEHICLE_LOCATIONS.find((loc) => loc.value === l)) ||
          "locationInvalid",
      ],
      mileage: [(a) => a === null || a > 0 || "mileagePositive"],
      status: [
        (a) => Boolean(a) || "required",
        (t) => t in VEHICLE_STATUS_TYPES || "statusInvalid",
      ],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(this.data[key]) === true)
    );
  }

  get isEmpty() {
    return Boolean(!this.id && isEmpty(diff(this.data, VEHICLE_FIELDS)));
  }

  isEqual(vehicle) {
    return Boolean(
      this.id === vehicle.id && isEmpty(diff(this.data, vehicle.data))
    );
  }

  // Formatting

  get type() {
    return VEHICLE_TYPES[this.data.type];
  }

  get location() {
    let location = VEHICLE_LOCATIONS.find(
      (l) => l.value === this.data.location
    );
    return location ? location.label : this.data.location;
  }

  get status() {
    const type = VEHICLE_STATUS_TYPES[this.data.status];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  toString() {
    return this.data.name;
  }
}
